import { Typography } from 'antd'
import { formatMessageDateTime } from '@/utils/datetime'
import type { MessageMedia } from '@/types/message'
import MessageMediaList from './MessageMediaList'

const { Text, Paragraph } = Typography

export interface WechatConversationMessage {
  message_id: string
  sender_name?: string
  send_time: string
  content_preview: string
  conversation_items?: Array<{
    sender_name?: string
    content: string
  }>
  media: MessageMedia[]
}

interface Props {
  messages: WechatConversationMessage[]
  pendingMediaUid?: string
  onQueueDownload: (messageId: string, mediaUid: string) => void
  emptyText?: string
}

function avatarText(senderName: string) {
  const trimmed = senderName.trim()
  return trimmed ? trimmed.slice(0, 1) : '?'
}

function chatrecordSenderName(senderName: string | undefined, index: number) {
  const value = senderName?.trim()
  if (value) return value
  return `合并转发消息 ${index + 1}`
}

export default function WechatConversation({
  messages,
  pendingMediaUid,
  onQueueDownload,
  emptyText = '无关联消息',
}: Props) {
  if (messages.length === 0) {
    return <Text type="secondary">{emptyText}</Text>
  }

  return (
    <div
      style={{
        display: 'grid',
        gap: 12,
        padding: 12,
        borderRadius: 6,
        background: '#f5f7fb',
      }}
    >
      {messages.map((msg) => {
        const senderName = msg.sender_name?.trim() || '未知发送人'
        const conversationItems = msg.conversation_items?.filter((item) => item.content.trim()) || []
        return (
          <div
            key={msg.message_id}
            style={{
              display: 'grid',
              gridTemplateColumns: '32px minmax(0, 1fr)',
              gap: 8,
              alignItems: 'start',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: 4,
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: '#52c41a',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {avatarText(senderName)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <Text strong>{senderName}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {formatMessageDateTime(msg.send_time)}
                </Text>
              </div>
              <div
                style={{
                  width: 'fit-content',
                  maxWidth: '100%',
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: '#fff',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                }}
              >
                {conversationItems.length > 0 ? (
                  <div style={{ display: 'grid', gap: 10, marginBottom: msg.media.length > 0 ? 10 : 0 }}>
                    {conversationItems.map((item, index) => {
                      const itemSender = chatrecordSenderName(item.sender_name, index)
                      return (
                        <div
                          key={`${msg.message_id}-${index}`}
                          style={{
                            display: 'grid',
                            gap: 4,
                            padding: '8px 10px',
                            borderRadius: 6,
                            background: '#f8fafc',
                            border: '1px solid #e5e7eb',
                          }}
                        >
                          <Text strong style={{ fontSize: 12 }}>{itemSender}</Text>
                          <Paragraph
                            style={{
                              marginBottom: 0,
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {item.content}
                          </Paragraph>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <Paragraph
                    style={{
                      marginBottom: msg.media.length > 0 ? 10 : 0,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {msg.content_preview || '无文本内容'}
                  </Paragraph>
                )}
                <MessageMediaList
                  items={msg.media}
                  pendingMediaUid={pendingMediaUid}
                  title={false}
                  onQueueDownload={(mediaUid) => onQueueDownload(msg.message_id, mediaUid)}
                />
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
